import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import {
  UserCircleIcon,
  BuildingOfficeIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  KeyIcon,
  EyeIcon,
  EyeSlashIcon,
  CheckCircleIcon,
  PencilSquareIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';
import { updateProfile, requestPasswordReset, verifyResetCode, resetPassword } from '../../services/authService';
import { getMe } from '../../store/slices/authSlice';

const Settings = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { user, lab } = useSelector((state) => state.auth);
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Profile form state
  const [profileForm, setProfileForm] = useState({
    labName: '',
    email: '',
    phone: '',
    address: '',
    postalCode: '',
    registrationNumber: '',
    licenseNumber: ''
  });

  // Password change state
  const [passwordStep, setPasswordStep] = useState('email'); // email, verification, newPassword
  const [passwordForm, setPasswordForm] = useState({
    email: '',
    verificationCode: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    if (user && lab) {
      setProfileForm({
        labName: lab.name || lab.labName || '',
        email: user.email || '',
        phone: user.phone || lab.phone || '',
        address: lab.address || '',
        postalCode: lab.postalCode || '',
        registrationNumber: lab.registrationNumber || '',
        licenseNumber: lab.licenseNumber || ''
      });
      setPasswordForm(prev => ({ ...prev, email: user.email || '' }));
    }
  }, [user, lab]);

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const updateData = {
        phone: profileForm.phone,
        lab: {
          name: profileForm.labName,
          address: profileForm.address,
          postalCode: profileForm.postalCode,
          registrationNumber: profileForm.registrationNumber,
          licenseNumber: profileForm.licenseNumber
        }
      };

      await updateProfile(updateData);
      // Refresh user data to get updated info
      await dispatch(getMe());
      toast.success(t('settings.profile.updateSuccess'));
    } catch (error) {
      console.error('Profile update error:', error);
      toast.error(t('settings.profile.updateError'));
    } finally {
      setLoading(false);
    }
  };

  const handleRequestPasswordReset = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await requestPasswordReset(passwordForm.email);
      setPasswordStep('verification');
      toast.success(t('settings.password.codeWasSent'));
    } catch (error) {
      console.error('Password reset request error:', error);
      toast.error(t('settings.password.sendCodeError'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await verifyResetCode(passwordForm.email, passwordForm.verificationCode);
      setPasswordStep('newPassword');
      toast.success(t('settings.password.codeVerified'));
    } catch (error) {
      console.error('Code verification error:', error);
      toast.error(t('settings.password.invalidCode'));
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error(t('settings.password.passwordMismatch'));
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      toast.error(t('settings.password.passwordMinLength'));
      return;
    }

    setLoading(true);

    try {
      await resetPassword(passwordForm.email, passwordForm.verificationCode, passwordForm.newPassword);
      setPasswordStep('email');
      setPasswordForm({
        email: user.email || '',
        verificationCode: '',
        newPassword: '',
        confirmPassword: ''
      });
      toast.success(t('settings.password.changeSuccess'));
    } catch (error) {
      console.error('Password reset error:', error);
      toast.error(t('settings.password.changeError'));
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    {
      id: 'profile',
      name: t('settings.tabs.profile'),
      icon: UserCircleIcon
    },
    {
      id: 'password',
      name: t('settings.tabs.password'),
      icon: KeyIcon
    }
  ];

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {t('settings.title')}
        </h1>
        <p className="text-gray-600">
          {t('settings.subtitle')}
        </p>
      </div>

      {/* Tabs */}
      <div className="mb-8">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-brand-blue text-brand-blue'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="w-5 h-5" />
                {tab.name}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Profile Tab */}
      {activeTab === 'profile' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <UserCircleIcon className="w-6 h-6 text-brand-blue" />
              <h2 className="text-xl font-semibold text-gray-900">
                {t('settings.profile.title')}
              </h2>
            </div>

            <form onSubmit={handleProfileSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Lab Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <BuildingOfficeIcon className="w-4 h-4 inline ml-2" />
                    {t('settings.profile.labName')}
                  </label>
                  <input
                    type="text"
                    value={profileForm.labName}
                    onChange={(e) => setProfileForm({ ...profileForm, labName: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent"
                    required
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <EnvelopeIcon className="w-4 h-4 inline ml-2" />
                    {t('settings.profile.email')}
                  </label>
                  <input
                    type="email"
                    value={profileForm.email}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                    disabled
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {t('settings.profile.emailNotEditable')}
                  </p>
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <PhoneIcon className="w-4 h-4 inline ml-2" />
                    {t('settings.profile.phone')}
                  </label>
                  <input
                    type="tel"
                    value={profileForm.phone}
                    onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent"
                    placeholder="05xxxxxxxx"
                    maxLength="10"
                  />
                </div>

                {/* Postal Code */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <MapPinIcon className="w-4 h-4 inline ml-2" />
                    {t('settings.profile.postalCode')}
                  </label>
                  <input
                    type="text"
                    value={profileForm.postalCode}
                    onChange={(e) => setProfileForm({ ...profileForm, postalCode: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent"
                    placeholder="12345"
                    maxLength="5"
                  />
                </div>

                {/* Registration Number */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <ShieldCheckIcon className="w-4 h-4 inline ml-2" />
                    {t('settings.profile.registrationNumber')}
                  </label>
                  <input
                    type="text"
                    value={profileForm.registrationNumber}
                    onChange={(e) => setProfileForm({ ...profileForm, registrationNumber: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent"
                    placeholder="1234567890"
                    maxLength="10"
                  />
                </div>

                {/* License Number */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <ShieldCheckIcon className="w-4 h-4 inline ml-2" />
                    {t('settings.profile.licenseNumber')}
                  </label>
                  <input
                    type="text"
                    value={profileForm.licenseNumber}
                    onChange={(e) => setProfileForm({ ...profileForm, licenseNumber: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent"
                    placeholder={t('labSettings.licenseNumberPlaceholder')}
                  />
                </div>
              </div>

              {/* Address */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <MapPinIcon className="w-4 h-4 inline ml-2" />
                  {t('settings.profile.address')}
                </label>
                <textarea
                  value={profileForm.address}
                  onChange={(e) => setProfileForm({ ...profileForm, address: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent"
                  placeholder={t('labSettings.fullAddressPlaceholder')}
                />
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-3 bg-gradient-to-r from-brand-blue to-brand-blueSecondary text-white rounded-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <PencilSquareIcon className="w-5 h-5" />
                  )}
                  {t('settings.profile.updateButton')}
                </button>
              </div>
            </form>
          </div>
        </motion.div>
      )}

      {/* Password Tab */}
      {activeTab === 'password' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <KeyIcon className="w-6 h-6 text-brand-blue" />
              <h2 className="text-xl font-semibold text-gray-900">
                {t('settings.password.title')}
              </h2>
            </div>

            {/* Step 1: Email Verification */}
            {passwordStep === 'email' && (
              <form onSubmit={handleRequestPasswordReset} className="space-y-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <ShieldCheckIcon className="w-5 h-5 text-blue-600" />
                    <p className="text-blue-800 text-sm">
                      {t('settings.password.securityNote')}
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <EnvelopeIcon className="w-4 h-4 inline ml-2" />
                    {t('settings.password.emailLabel')}
                  </label>
                  <input
                    type="email"
                    value={passwordForm.email}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                    disabled
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {t('settings.password.emailConfirmation')}
                  </p>
                </div>

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-6 py-3 bg-gradient-to-r from-brand-blue to-brand-blueSecondary text-white rounded-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {loading ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <EnvelopeIcon className="w-5 h-5" />
                    )}
                    {t('settings.password.sendCodeButton')}
                  </button>
                </div>
              </form>
            )}

            {/* Step 2: Verification Code */}
            {passwordStep === 'verification' && (
              <form onSubmit={handleVerifyCode} className="space-y-6">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <CheckCircleIcon className="w-5 h-5 text-green-600" />
                    <p className="text-green-800 text-sm">
                      {t('settings.password.codeSentMessage')}
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('settings.password.verificationCodeLabel')}
                  </label>
                  <input
                    type="text"
                    value={passwordForm.verificationCode}
                    onChange={(e) => setPasswordForm({ ...passwordForm, verificationCode: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent text-center text-2xl tracking-widest"
                    placeholder="------"
                    maxLength={6}
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {t('settings.password.codeInstructions')}
                  </p>
                </div>

                <div className="flex justify-between">
                  <button
                    type="button"
                    onClick={() => setPasswordStep('email')}
                    className="px-6 py-3 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-all"
                  >
                    {t('common.back')}
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-6 py-3 bg-gradient-to-r from-brand-blue to-brand-blueSecondary text-white rounded-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {loading ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <CheckCircleIcon className="w-5 h-5" />
                    )}
                    {t('settings.password.verifyCodeButton')}
                  </button>
                </div>
              </form>
            )}

            {/* Step 3: New Password */}
            {passwordStep === 'newPassword' && (
              <form onSubmit={handlePasswordReset} className="space-y-6">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <CheckCircleIcon className="w-5 h-5 text-green-600" />
                    <p className="text-green-800 text-sm">
                      {t('settings.password.codeVerifiedMessage')}
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('settings.password.newPasswordLabel')}
                  </label>
                  <div className="relative">
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      value={passwordForm.newPassword}
                      onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent pl-12"
                      placeholder={t('settings.password.newPasswordPlaceholder')}
                      minLength={6}
                      required
                    />
                    <KeyIcon className="absolute right-4 top-3.5 h-5 w-5 text-gray-400" />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute left-4 top-3.5 text-gray-400 hover:text-gray-600"
                    >
                      {showNewPassword ? (
                        <EyeSlashIcon className="h-5 w-5" />
                      ) : (
                        <EyeIcon className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('settings.password.confirmPasswordLabel')}
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={passwordForm.confirmPassword}
                      onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent pl-12"
                      placeholder={t('settings.password.confirmPasswordPlaceholder')}
                      minLength={6}
                      required
                    />
                    <KeyIcon className="absolute right-4 top-3.5 h-5 w-5 text-gray-400" />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute left-4 top-3.5 text-gray-400 hover:text-gray-600"
                    >
                      {showConfirmPassword ? (
                        <EyeSlashIcon className="h-5 w-5" />
                      ) : (
                        <EyeIcon className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>

                <div className="flex justify-between">
                  <button
                    type="button"
                    onClick={() => setPasswordStep('verification')}
                    className="px-6 py-3 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-all"
                  >
                    {t('common.back')}
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-6 py-3 bg-gradient-to-r from-brand-blue to-brand-blueSecondary text-white rounded-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {loading ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <KeyIcon className="w-5 h-5" />
                    )}
                    {t('settings.password.changePasswordButton')}
                  </button>
                </div>
              </form>
            )}
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default Settings;
